import * as React from "react";
import IOrderPage, { TOrderPage } from "./IOrderPage";
import { connect } from "react-redux";
import Header from "@appComponents/Header/Header";
import OrdersList from "@market/components/OrdersList/OrdersList";
import RiskNotice from "@market/components/RiskNotice/RiskNotice";
import ShareDetails from "@market/components/ShareDetails/ShareDetails";
import {
  newCompanyPhoto,
  sortOrdersByDateAsc,
  sortOrdersByPriceDesc,
  sortOrdersByPriceAsc,
} from "@market/features/helpers/functions";
import ToolTip from "@appComponents/ToolTip/ToolTip";
import { Link } from "react-router-dom";
import SMAccessValidationTypes from "@market/enums/SMAccessValidationTypes";
import { setAssetDetails } from "@market/features/action.market";
import Loader from "@appComponents/Loader/Loader";
import { EordersTypes } from "@market/enums/OrdersTypes";
import Big, { BigConstructor } from "big.js";
import { getMinutesFromTimestamp } from "@features/utils/functions";
import { MyLink } from "@features/utils/reactFunctions";
import ISPV from "@market/components/SPVsList/ISPV";
import onlyLoggedInHOC from "@appComponents/OnlyLoggedInHOC/OnlyLoggedIn";

import * as style from "./OrderPageStyle.module.scss";
import * as general from "@appStyles/general.module.scss";
import * as elements from "@appStyles/_elements.module.scss";
import * as tooltipStyle from "@appComponents/ToolTip/ToolTipStyle.module.scss";
import TradeHistory from "@appComponents/TradeHistory/TradeHistory";

export class OrderPage extends React.Component<IOrderPage, TOrderPage> {
  state: TOrderPage = {
    companyDesc: false,
    loader: this.props.spv && this.props.tradesList ? false : true,
    tradesList: undefined,
  };

  private tooltipSellOrderMsg: string[];
  private tooltipBuyOrderMsg: string[];
  private tradeSectionRef: React.RefObject<HTMLDivElement> =
    React.createRef<HTMLDivElement>();

  private big: BigConstructor;

  constructor(props: IOrderPage) {
    super(props);
    this.big = Big();
    this.big.RM = 0;
  }

  componentDidMount() {
    // call only if user changed submenu tab
    if (this.props.spv && this.props.timestamp) {
      const spvId = this.props.match.params.asset_id;

      const minutes = getMinutesFromTimestamp(
        Date.now() - this.props.timestamp
      );
      if (minutes >= 2) {
        this.props.setAssetDetails(this.props.spv.trading_asset_id, spvId);
      }
    }
  }

  componentDidUpdate(prevProps: IOrderPage) {
    if (this.state.loader && this.props.spv && this.props.tradesList) {
      this.setState({
        loader: false,
      });
    }
  }

  renderPlaceBuyOrderBtn(disabled: boolean) {
    if (disabled) {
      return (
        <>
          <ToolTip
            text={
              this.tooltipBuyOrderMsg[0]
                ? `${this.tooltipBuyOrderMsg[0]}`
                : "Currently you can have only one open buy order per asset."
            }
            className={tooltipStyle.tooltipPurple}
          >
            <a
              className={[
                elements.btn,
                elements.btnDisabled,
                style.ctaSell,
                style.ctaDisabled,
              ].join(" ")}
              data-cy="place-buy-order"
            >
              Place buy order
            </a>
          </ToolTip>
        </>
      );
    }

    return (
      <Link
        className={[elements.btn, style.ctaSell].join(" ")}
        to={`orders/buy`}
        data-cy="place-buy-order"
      >
        Place buy order
      </Link>
    );
  }

  renderPlaceSellOrderBtn(disabled: boolean) {
    if (disabled) {
      return (
        <>
          <ToolTip
            text={`${this.tooltipSellOrderMsg[0]}`}
            className={tooltipStyle.tooltipPurple}
          >
            <a
              className={[
                elements.btn,
                elements.btnDisabled,
                style.ctaSell,
                style.ctaDisabled,
              ].join(" ")}
              data-cy="sell-shares"
            >
              Sell Shares
            </a>
          </ToolTip>
        </>
      );
    }

    return (
      <Link
        className={[elements.btn, style.ctaSell].join(" ")}
        to={`orders/sell`}
        data-cy="sell-shares"
      >
        Sell Shares
      </Link>
    );
  }

  toggleCompanyDesc = () => {
    this.setState({
      companyDesc: !this.state.companyDesc,
    });
  };

  buildTooltipMessages(reason: SMAccessValidationTypes[]) {
    this.tooltipSellOrderMsg = [];
    this.tooltipBuyOrderMsg = [];

    reason.map((message) => {
      if (message === SMAccessValidationTypes.has_active_buy_order) {
        this.tooltipBuyOrderMsg.push(
          `Currently you can have only one open buy order per asset.`
        );
      }
      if (message === SMAccessValidationTypes.no_shares_for_sell) {
        this.tooltipSellOrderMsg.push(
          `You don’t have any [${this.props.spv.name}] shares yet to sell`
        );
      }
      if (message === SMAccessValidationTypes.has_active_sell_order) {
        this.tooltipSellOrderMsg.push(
          `Currently you can have only one open sell order per asset.`
        );
      }
      if (message === SMAccessValidationTypes.no_sellable_shares) {
        this.tooltipSellOrderMsg.push(
          `You will be able to place a sell order for your [${
            this.props.spv.name
          }]${" "}
           shares after the 1 year lock-in period ends.`
        );
      }
    });
  }

  isBuyDisabled() {
    if (!this.props.spv || !this.props.userInfo) return false;
    const { orderShares } = this.props.spv;
    const hasActiveBuy = orderShares ? orderShares.has_active_buy_orders : true;
    const reason: SMAccessValidationTypes[] = [];

    if (hasActiveBuy) reason.push(SMAccessValidationTypes.has_active_buy_order);

    if (reason.length !== 0) {
      this.buildTooltipMessages(reason);
      return true;
    }

    return false;
  }

  isSellDisabled() {
    if (!this.props.spv || !this.props.userInfo) return false;
    const { orderShares } = this.props.spv;

    const hasAnyShares = this.getAssetsShares().total;
    const hasActiveSell = orderShares
      ? orderShares.has_active_sell_orders
      : true;
    const hasSellableShares = this.getAssetsShares().sellable;

    const reason: SMAccessValidationTypes[] = [];
    if (!hasAnyShares) reason.push(SMAccessValidationTypes.no_shares_for_sell);
    if (hasActiveSell)
      reason.push(SMAccessValidationTypes.has_active_sell_order);
    if (!hasSellableShares)
      reason.push(SMAccessValidationTypes.no_sellable_shares);

    if (reason.length !== 0) {
      this.buildTooltipMessages(reason);
      return true;
    }

    return false;
  }

  scrollToTrades = () => {
    if (this.tradeSectionRef && this.tradeSectionRef.current) {
      window.scrollTo(0, this.tradeSectionRef.current.offsetTop - 50);
    }
  };

  getAssetsShares = () => {
    if (this.props.spv.orderShares) {
      return this.props.spv.orderShares.assets.reduce(
        (tally, curr) => {
          tally.total = Number(
            this.big(tally.total).add(curr.total_number_of_shares)
          );
          tally.sellable = Number(
            this.big(tally.sellable).add(curr.sellable_number_of_shares)
          );

          return tally;
        },
        { total: 0, sellable: 0 }
      );
    }

    return { total: 0, sellable: 0 };
  };

  render() {
    if (!this.props.spv || !this.props.userInfo || this.state.loader) {
      return <Loader moreSpace={true} />;
    }

    const { detailedCompanyAsset, sellOrders, orderShares } = this.props.spv;
    if (!detailedCompanyAsset || !sellOrders || !orderShares) {
      return <Loader moreSpace={true} />;
    }

    const spvId = this.props.spv.id;
    const investorId = this.props.userInfo.investor_id;
    const isPlaceBuyOrderDisabled = this.isBuyDisabled();
    const isPlaceSellOrderDisabled = this.isSellDisabled();

    return (
      <div>
        <Header>
          <div className={style.scrollToTrades} onClick={this.scrollToTrades}>
            Show trading
          </div>
          <div className={style.headerContent}>
            <div>
              <div className={style.companySpvMain}>
                <a href={`/companies/${this.props.spv.company_slug}`}>
                  <div
                    className={style.companyAvatar}
                    style={newCompanyPhoto(this.props.spv.company_avatar)}
                  />
                </a>
                <div className={style.companySpvContainer}>
                  <h2 className={style.companySpvName}>
                    {this.props.spv.name}
                  </h2>
                  <h3 className={style.companyName}>
                    <MyLink
                      to={`/companies/${this.props.spv.company_slug}`}
                      className={style.companyLink}
                      isReactLink={true}
                    >
                      {` ${this.props.spv.company_name} (${detailedCompanyAsset.company_legal_name}) `}
                    </MyLink>
                    {detailedCompanyAsset.description && (
                      <a
                        onClick={this.toggleCompanyDesc}
                        className={style.toggleCompanyDesc}
                      >
                        {!this.state.companyDesc ? "Show " : "Hide "}description
                      </a>
                    )}
                  </h3>
                </div>
              </div>
              {detailedCompanyAsset.description && this.state.companyDesc && (
                <div className={`${style.handleAnimation}`}>
                  <div id="companyDescription" className={`${style.fadeInUp}`}>
                    <p className={style.companyAbout}>
                      {detailedCompanyAsset.description}
                    </p>
                  </div>
                </div>
              )}
            </div>
            <div className={style.cta}>
              {this.renderPlaceSellOrderBtn(isPlaceSellOrderDisabled)}
              {this.renderPlaceBuyOrderBtn(isPlaceBuyOrderDisabled)}
            </div>
            <div>
              <ShareDetails
                detailedCompanyAsset={detailedCompanyAsset}
                orderShares={orderShares}
                last_trade_price={this.props.spv.last_trade_price}
                scrollToTrades={this.scrollToTrades}
                tradesList={this.props.tradesList || []}
                lastSharePrice={this.props.lastSharePrice}
                assetsShares={this.getAssetsShares()}
              />
            </div>
          </div>
        </Header>

        <div
          className={[
            general.appContainer,
            general.appContainerTransparent,
            style.sharesContainer,
          ].join(" ")}
        >
          <div className={style.orders}>
            <OrdersList
              orders={this.props.spv.buyOrders!.sort(sortOrdersByPriceDesc)}
              investorId={investorId}
              companySlug={this.props.spv.company_slug}
              type={EordersTypes.buy}
              sellingEnabled={this.getAssetsShares().sellable > 0}
              spvId={spvId}
              lastSharePrice={this.props.lastSharePrice}
              priceFloor={this.props.spv.detailedCompanyAsset?.price_floor}
              priceCap={
                this.props.spv.detailedCompanyAsset?.price_cap_enabled
                  ? this.props.spv.detailedCompanyAsset?.price_cap
                  : null
              }
              isPlaceBuyOrderDisabled={isPlaceBuyOrderDisabled}
            />
            <OrdersList
              orders={this.props.spv.sellOrders!.sort(sortOrdersByPriceAsc)}
              investorId={investorId}
              companySlug={this.props.spv.company_slug}
              type={EordersTypes.sell}
              spvId={spvId}
              priceCap={
                this.props.spv.detailedCompanyAsset?.price_cap_enabled
                  ? this.props.spv.detailedCompanyAsset?.price_cap
                  : null
              }
              isPlaceSellOrderDisabled={isPlaceSellOrderDisabled}
            />
            <div
              className={style.transactionHistory}
              ref={this.tradeSectionRef}
            >
              <TradeHistory
                tradesList={this.props.tradesList || []}
                assetId={this.props.match.params.asset_fid}
              />
            </div>
          </div>
        </div>
        <RiskNotice isColumn={true} />
      </div>
    );
  }
}

const mapStateToProps = (store: any, ownProps: IOrderPage) => {
  return {
    spv: store.secondaryMarketReducer.spvs
      ? store.secondaryMarketReducer.spvs.find(
          (spv: ISPV) => spv.id === Number(ownProps.match.params.asset_id)
        )
      : store.secondaryMarketReducer.spvs,
    userInfo: store.userInfo,
    tradesList: store.secondaryMarketReducer.tradesHistory,
    lastSharePrice:
      store.companyReducer.funding.length > 0
        ? store.companyReducer.funding[0].share_price
        : 0,
    timestamp: store.secondaryMarketReducer.timestamp,
  };
};

const mapDispatchToProps = (dispatch: Function) => {
  return {
    setAssetDetails: (assetId: number, spvId: number): Promise<any> => {
      return dispatch(setAssetDetails(assetId, spvId));
    },
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(onlyLoggedInHOC(OrderPage));
